import cx from 'classnames'
import { useState, useRef } from 'react'

import Styles from './SelectedPlanForm.module.scss'

import { AutopilotPlan, StripePrice } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'
import { nameMap, iconMap } from '../../../Plans/components/PlanDetails/PlanDetails'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Pressable } from '@react-aria/interactions'
import { Button } from 'blaze-ui/Button'
import { StripePaymentForm, StripePaymentFormRef } from 'components/StripePaymentForm/StripePaymentForm'
import { OnStartPlan } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { useRootStore } from 'stores/RootStore'
import { useSetupIntent } from 'components/TimeTriggerPaywall/screens/Checkout/Checkout'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { calculateExtraAccountCharges } from 'components/Settings/TieredBilling/utils'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { EnableAutoReload } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/components/EnableAutoReload/EnableAutoReload'

export const SelectedPlanForm = ({
  selectedPlan,
  currentPrice,
  onBack,
  onStartPlan,
  shouldAutoReloadCredits,
  toggleAutoReloadCredits,
}: {
  selectedPlan: AutopilotPlan
  currentPrice: StripePrice | null
  onBack: () => void
  onStartPlan: OnStartPlan
  shouldAutoReloadCredits: boolean
  toggleAutoReloadCredits: () => void
}) => {
  const price = currentPrice?.product === selectedPlan.id ? currentPrice : selectedPlan.prices[3] // 12-month plan as default
  const tierUsageStore = useRootStore().tierUsageStore
  const { currentSubscription } = tierUsageStore
  const [selectedPrice, setSelectedPrice] = useState<StripePrice | null>(price)
  const [isProcessing, setIsProcessing] = useState(false)
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { data: clientSecret, isLoading } = useSetupIntent(currentWorkspaceId)
  const stripeFormRef = useRef<StripePaymentFormRef>(null)
  const isAutoReloadCreditsEnabled = useFlipperFlag('auto_reload_credits')

  const Icon = iconMap[selectedPlan.key]

  const currentPriceIsSelected =
    selectedPrice?.id === currentPrice?.id &&
    currentSubscription?.subscription_payment_method !== null &&
    currentSubscription?.status === 'active' &&
    !currentSubscription?.cancel_at

  const handlePaymentMethodCreated = async (paymentMethodId?: string) => {
    if (!selectedPrice) return

    try {
      await onStartPlan({ paymentMethodId, chosenPriceId: selectedPrice.id })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start plan'
      showToastMessage(errorMessage, 'error')
      setIsProcessing(false)
    }
  }

  const handleError = (error: string) => {
    showToastMessage(error, 'error')
    setIsProcessing(false)
  }

  const handleStartPlan = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (stripeFormRef.current) {
      setIsProcessing(true)
      await stripeFormRef.current.submitPayment()
    }
  }

  return (
    <div className={Styles.container}>
      <div className={cx(Styles.chosenPlan, Styles[selectedPlan.key])}>
        <Icon color="var(--light-100)" />
        <Heading level={4} color="var(--light-100)">
          You selected the {nameMap[selectedPlan.key]} plan
        </Heading>
      </div>
      <Text>The longer Autopilot runs, the more you grow and the more you save.</Text>
      <div className={Styles.pricesContainer}>
        {selectedPlan.prices.map((price) => {
          const extraAccountCharges = calculateExtraAccountCharges(currentSubscription, price.recurring.interval_count)
          const basePriceMonthly = Math.ceil(price.unit_amount / 100 / price.recurring?.interval_count)
          const extraAccountMonthly = Math.ceil(extraAccountCharges / price.recurring.interval_count)
          const totalMonthly = basePriceMonthly + extraAccountMonthly
          const totalPrice = price.unit_amount / 100 + extraAccountCharges

          return (
            <Pressable onPress={() => setSelectedPrice(price)} key={price.id}>
              <div className={cx(Styles.price, { [Styles.selected]: price.id === selectedPrice?.id })}>
                <Text variant="smallList">
                  {price.recurring.interval_count === 1 ? 'Monthly' : `${price.recurring.interval_count} months`}
                </Text>
                <div className={Styles.priceAmount}>
                  <Heading level={4}>${totalMonthly}</Heading>

                  <Text variant="placeholder">/mo</Text>
                </div>
                {price.recurring.interval_count > 1 && (
                  <Text variant="metadata" color="var(--dark-40)">
                    ${totalPrice.toLocaleString()} total
                  </Text>
                )}
                {price.metadata.percent_off && (
                  <span className={Styles.percentOffPill}>{price.metadata.percent_off}% off</span>
                )}
              </div>
            </Pressable>
          )
        })}
      </div>
      {isAutoReloadCreditsEnabled && (
        <EnableAutoReload
          shouldAutoReloadCredits={shouldAutoReloadCredits}
          toggleAutoReloadCredits={toggleAutoReloadCredits}
          name="selected-plan-enable-auto-reload-credits"
        />
      )}
      {selectedPrice && (
        <form onSubmit={handleStartPlan}>
          <div className={Styles.creditCardForm}>
            <StripePaymentForm
              ref={stripeFormRef}
              clientSecret={clientSecret}
              onPaymentMethodCreated={handlePaymentMethodCreated}
              onError={handleError}
              isProcessing={isProcessing}
              setIsProcessing={setIsProcessing}
              disabled={currentPriceIsSelected}
            />
          </div>

          <div className={Styles.buttonContainer}>
            <Button variant="tertiary" size="lg" onPress={onBack}>
              Change Plan
            </Button>
            <div className={Styles.rightButtonsContainer}>
              <Text variant="placeholder" color="var(--dark-80)">
                $
                {selectedPrice.unit_amount / 100 +
                  calculateExtraAccountCharges(currentSubscription, selectedPrice.recurring.interval_count)}{' '}
                {selectedPrice.recurring.interval_count > 1
                  ? `every ${selectedPrice.recurring.interval_count} months`
                  : 'monthly'}
              </Text>
              <Button
                size="lg"
                type="submit"
                isDisabled={isProcessing || isLoading || !clientSecret || currentPriceIsSelected}
              >
                Pay Now
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  )
}
